import { useCallback, useMemo } from "react";
import { FileUploader } from "@themes/pollinator/ui/FileUploader";
import type { FileType, AllowedFileTypes } from "@core/types/file.types";
import type { QuizUploadedMedia } from "@core/features/quiz";

export type Props = {
  /** Field name for identification */
  name: string;
  /** Current value (array of media IDs) */
  value?: number[];
  /** Change handler */
  onChange: (value: number[]) => void;
  /** Blur handler */
  onBlur: () => void;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field has an error */
  error?: boolean;
  /** Validation rules for file constraints */
  validationRules?: {
    allowed_file_types?: string[];
    max_file_size?: number;
    max_files?: number;
  } | null;
  /** Maximum number of files (from question.maximum_answers) */
  maxFiles?: number;
  /** Question type for default configurations */
  questionType: "image" | "file";
  /** Upload file handler */
  uploadFileHandler: (
    file: File,
    onProgress: (progress: number) => void
  ) => Promise<Partial<FileType>>;
  /** Alt text change handler */
  onAltTextChange?: (mediaId: number, altText: string) => void;
  /** Initial files from previously uploaded media */
  initialFiles?: QuizUploadedMedia[];
  /** Test ID */
  "data-testid"?: string;
};

// Default file type configurations
const DEFAULT_ALLOWED_TYPES: Record<"image" | "file", AllowedFileTypes> = {
  image: {
    "image/jpeg": true,
    "image/png": true,
    "image/gif": true,
    "image/svg+xml": true,
    "image/webp": true,
  },
  file: {
    "image/jpeg": true,
    "image/png": true,
    "image/gif": true,
    "image/svg+xml": true,
    "application/pdf": true,
    "application/msword": true,
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      true,
    "application/vnd.ms-excel": true,
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": true,
    "text/plain": true,
  },
};

const DEFAULT_DESCRIPTIONS: Record<"image" | "file", string> = {
  image: "Images (JPG, PNG, GIF, SVG, WebP)",
  file: "Documents (PDF, DOC, DOCX, XLS, XLSX, TXT) and Images",
};

const MIME_TO_LABEL: Record<string, { category: string; label: string }> = {
  "image/jpeg": { category: "Images", label: "JPG" },
  "image/png": { category: "Images", label: "PNG" },
  "image/gif": { category: "Images", label: "GIF" },
  "image/svg+xml": { category: "Images", label: "SVG" },
  "image/webp": { category: "Images", label: "WebP" },
  "application/pdf": { category: "Documents", label: "PDF" },
  "application/msword": { category: "Documents", label: "DOC" },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": {
    category: "Documents",
    label: "DOCX",
  },
  "application/vnd.ms-excel": { category: "Documents", label: "XLS" },
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
    category: "Documents",
    label: "XLSX",
  },
  "text/plain": { category: "Documents", label: "TXT" },
  "audio/mpeg": { category: "Audio", label: "MP3" },
  "audio/wav": { category: "Audio", label: "WAV" },
  "audio/ogg": { category: "Audio", label: "OGG" },
  "audio/webm": { category: "Audio", label: "WebM" },
  "audio/aac": { category: "Audio", label: "AAC" },
  "audio/mp4": { category: "Audio", label: "M4A" },
  "audio/x-m4a": { category: "Audio", label: "M4A" },
};

function describeFileTypes(mimeTypes: string[]): string {
  const groups: Record<string, string[]> = {};
  for (const mime of mimeTypes) {
    const info = MIME_TO_LABEL[mime];
    if (info) {
      if (!groups[info.category]) groups[info.category] = [];
      groups[info.category].push(info.label);
    }
  }
  return Object.entries(groups)
    .map(([category, labels]) => `${category} (${labels.join(", ")})`)
    .join(" and ");
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * File answer input for quiz questions.
 * Presentational component that wraps FileUploader for quiz form integration.
 *
 * This is a pure UI component - business logic is handled by FileAnswerInputContainer.
 *
 * @example
 * <FileAnswerInput
 *   name="question-123"
 *   value={[1, 2, 3]}
 *   onChange={(ids) => console.log(ids)}
 *   onBlur={() => {}}
 *   questionType="image"
 *   uploadFileHandler={async (file, onProgress) => { ... }}
 * />
 */
/**
 * Converts API media objects to FileType format for display in FileUploader.
 */
function convertToFileTypes(media: QuizUploadedMedia[]): FileType[] {
  return media.map((m) => ({
    id: m.id,
    name: m.name,
    size: String(m.size),
    progress: 100,
    error: false,
    checked: false,
    file: m.file,
    altText: m.alt_text,
    mimeType: m.file_type === "image" ? "image/jpeg" : "application/octet-stream",
  }));
}

export function FileAnswerInput({
  name,
  // value is used by React Hook Form but not directly here
  value: _value = [],
  onChange,
  onBlur,
  // disabled is passed but FileUploader doesn't support it yet
  disabled: _disabled = false,
  validationRules: validationRulesProp,
  maxFiles = 1,
  questionType,
  uploadFileHandler,
  onAltTextChange,
  initialFiles = [],
  "data-testid": testId = "file-answer-input",
}: Props) {
  const validationRules = validationRulesProp ?? {};

  // Convert initial files to FileType format for display
  const convertedInitialFiles = useMemo(
    () => convertToFileTypes(initialFiles),
    [initialFiles]
  );

  // Build allowed file types from validation rules or defaults
  const allowedFileTypes: AllowedFileTypes = useMemo(() => {
    if (validationRules.allowed_file_types?.length) {
      return validationRules.allowed_file_types.reduce(
        (acc, type) => {
          acc[type] = true;
          return acc;
        },
        {} as AllowedFileTypes
      );
    }
    return DEFAULT_ALLOWED_TYPES[questionType];
  }, [validationRules.allowed_file_types, questionType]);

  // Compute description dynamically from allowed file types
  const description = useMemo(() => {
    const base = validationRules.allowed_file_types?.length
      ? describeFileTypes(validationRules.allowed_file_types)
      : DEFAULT_DESCRIPTIONS[questionType];
    if (validationRules.max_file_size) {
      return `${base} - Max ${formatBytes(validationRules.max_file_size)}`;
    }
    return base;
  }, [validationRules.allowed_file_types, validationRules.max_file_size, questionType]);

  // Handle files change from FileUploader
  const handleUploadedFilesChange = useCallback(
    (files: FileType[]) => {
      // Extract media IDs from successfully uploaded files
      const mediaIds = files
        .filter((f) => f.progress === 100 && !f.error && f.id)
        .map((f) => Number(f.id));

      onChange(mediaIds);
    },
    [onChange]
  );

  // Handle all files processed
  const handleAllFilesHandled = useCallback(() => {
    onBlur();
  }, [onBlur]);

  return (
    <FileUploader
      hash={name}
      uploadFileHandler={uploadFileHandler}
      onUploadedFilesChange={handleUploadedFilesChange}
      onAllFilesHandled={handleAllFilesHandled}
      onAltTextChange={onAltTextChange}
      allowedFileTypes={allowedFileTypes}
      acceptedFileTypes={description}
      maxSize={validationRules.max_file_size}
      initialFiles={convertedInitialFiles}
      maxFiles={maxFiles}
      showRemoveButton
      data-testid={testId}
    />
  );
}

export default FileAnswerInput;
